import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Info, AlertTriangle, Zap, ExternalLink, CheckCheck } from "lucide-react";

const PRIORITY_META = {
  info:    { Icon: Info,          color: "text-sky-700",   bg: "bg-sky-50",    label: "Info"    },
  warning: { Icon: AlertTriangle, color: "text-amber-700", bg: "bg-amber-50",  label: "Warning" },
  urgent:  { Icon: Zap,           color: "text-rose-700",  bg: "bg-rose-50",   label: "Urgent"  },
};

function fmt(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString();
}

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const res = await api.get("/notifications", { params: { page: p, page_size: 20 } });
      setItems(res.data?.items || []);
      setTotalPages(res.data?.total_pages || 1);
      setPage(res.data?.page || 1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(1); }, []);

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      toast.success("All notifications marked as read");
      await load(page);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleRowClick = async (it) => {
    if (!it.read_at) {
      try {
        await api.post(`/notifications/${it.notification_id}/read`);
        setItems((arr) => arr.map((x) => x.notification_id === it.notification_id ? { ...x, read_at: new Date().toISOString() } : x));
      } catch { /* ignore */ }
    }
    if (it.link) navigate(it.link);
  };

  const unreadCount = items.filter((i) => !i.read_at).length;

  return (
    <>
      <div className="space-y-6" data-testid="notifications-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">Notifications</h1>
            <p className="text-sm text-brand-inkMuted mt-1">Updates and announcements from the platform team.</p>
          </div>
          <Button
            variant="outline"
            onClick={markAll}
            disabled={unreadCount === 0}
            className="rounded-full"
            data-testid="notifications-mark-all-btn"
          >
            <CheckCheck className="w-4 h-4 mr-2" /> Mark all read
          </Button>
        </div>

        {loading ? (
          <div className="bg-white rounded-3xl border border-[#2563EB]/10 p-10 text-center text-brand-inkMuted">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl border border-[#2563EB]/10 p-10 text-center" data-testid="notifications-empty">
            <p className="font-heading font-bold text-foreground text-lg">You're all caught up</p>
            <p className="text-sm text-brand-inkMuted mt-1">No notifications yet.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="notifications-list">
            {items.map((it) => {
              const meta = PRIORITY_META[it.priority] || PRIORITY_META.info;
              return (
                <button
                  key={it.recipient_id}
                  type="button"
                  onClick={() => handleRowClick(it)}
                  className={`w-full text-left bg-white rounded-3xl border ${it.read_at ? "border-slate-200" : "border-[#2563EB]/30 ring-1 ring-[#2563EB]/15"} p-5 hover:-translate-y-0.5 hover:shadow-md transition-all`}
                  data-testid={`notification-row-${it.notification_id}`}
                >
                  <div className="flex gap-4">
                    <div className={`shrink-0 w-11 h-11 rounded-2xl ${meta.bg} ${meta.color} flex items-center justify-center`}>
                      <meta.Icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-heading font-semibold text-foreground">{it.title}</span>
                        <span className={`text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full ${meta.bg} ${meta.color}`}>
                          {meta.label}
                        </span>
                        {!it.read_at && <span className="text-[11px] font-semibold text-rose-600">• New</span>}
                      </div>
                      <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{it.message}</p>
                      <div className="flex items-center gap-3 text-xs text-brand-inkMuted mt-2">
                        <span>From {it.sender_name || "Platform"}</span>
                        <span>•</span>
                        <span>{fmt(it.created_at)}</span>
                        {it.link && (
                          <>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1 text-[#1D4ED8] font-medium">
                              Open <ExternalLink className="w-3 h-3" />
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 pt-2">
            <Button variant="outline" disabled={page <= 1} onClick={() => load(page - 1)} data-testid="notifications-prev-btn" className="rounded-full">Previous</Button>
            <span className="text-sm text-brand-inkMuted">Page {page} of {totalPages}</span>
            <Button variant="outline" disabled={page >= totalPages} onClick={() => load(page + 1)} data-testid="notifications-next-btn" className="rounded-full">Next</Button>
          </div>
        )}
      </div>
    </>
  );
}
